import { useEffect, useState, useRef } from 'react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Play, Pause, Square, Send, AlertTriangle, Clock, LogOut, CheckCircle, Edit3, CalendarOff, Calendar, Plus, ChevronRight, Cake, RotateCcw, ArrowRightLeft, Download } from 'lucide-react';
import { CreateTaskDialog } from '../components/CreateTaskDialog';
import api from '../utils/api';
import { toast } from 'sonner';
import { DailyTipCard } from '../components/DailyTipCard';
import { CelebrationsBanner } from '../components/CelebrationsBanner';
import { MyProfileDatesDialog } from '../components/MyProfileDatesDialog';
import { UpcomingCelebrationsCard } from '../components/UpcomingCelebrationsCard';
import { MyDaySummaryCard } from '../components/MyDaySummaryCard';
import { CrossTeamTasksCard } from '../components/CrossTeamTasksCard';
import { STATUS_COLORS, STATUS_LABELS } from '../utils/constants';
import { AutoPendingBadge } from '../components/AutoPendingBadge';

const STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-500' },
  { value: 'pending_with_client', label: 'Pending w/ Client', color: 'bg-orange-500' },
  { value: 'pending_with_member', label: 'Pending w/ Member', color: 'bg-purple-500' },
  { value: 'on_hold_client', label: 'On Hold', color: 'bg-amber-500' },
  { value: 'completed', label: 'Completed', color: 'bg-green-500' },
];
const MANUAL_REASONS = [
  { value: 'client_visit', label: 'Client Visit' },
  { value: 'forgot_to_input', label: 'Forgot to Input' },
  { value: 'downtime', label: 'Downtime' },
];

export default function MemberWorkspace() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTimers, setActiveTimers] = useState({});
  const [timerDisplays, setTimerDisplays] = useState({});
  const [commentTaskId, setCommentTaskId] = useState(null);
  const [quickComment, setQuickComment] = useState('');
  const [pwmExpandedTaskId, setPwmExpandedTaskId] = useState(null);
  const [showMyDates, setShowMyDates] = useState(false);
  const [showEodDialog, setShowEodDialog] = useState(false);
  // Transfer dialog state (Jun 2026 — members can now hand a task to a teammate
  // with reviewer-style accept/reject before it actually moves).
  const [transferTask, setTransferTask] = useState(null);
  const [transferToId, setTransferToId] = useState('');
  const [transferComment, setTransferComment] = useState('');
  // Manual time entry state
  const [manualTaskId, setManualTaskId] = useState(null);
  const [manualHours, setManualHours] = useState('');
  const [manualReason, setManualReason] = useState('');
  const [manualDesc, setManualDesc] = useState('');
  const [manualDate, setManualDate] = useState('today');
  // Leave state
  const [showLeavePrompt, setShowLeavePrompt] = useState(false);
  const [leaveDate, setLeaveDate] = useState('');
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [services, setServices] = useState([]);
  const [showCreateTask, setShowCreateTask] = useState(false);
  // Client time logs (downtime / meetings / other)
  const [showCtlDialog, setShowCtlDialog] = useState(false);
  const [ctlForm, setCtlForm] = useState({ client_id: '', category: 'meeting', log_date: '', hours: '', description: '' });
  const [savingCtl, setSavingCtl] = useState(false);
  const [myCtlLogs, setMyCtlLogs] = useState([]);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { fetchData(); checkPreviousDay(); }, []);

  // Auto-refresh when WebSocket notification arrives
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('ws-notification', handler);
    return () => window.removeEventListener('ws-notification', handler);
  }, []);

  // One-time per-session nudge: if my DOB & joining date are both blank,
  // toast me with a quick action to fill them in.
  useEffect(() => {
    try {
      if (!users.length || !currentUser.id) return;
      const me = users.find(u => u.id === currentUser.id);
      if (!me) return;
      if (me.date_of_birth || me.joining_date) return;
      const nudgeKey = `dates-nudge-shown-${me.id}`;
      if (sessionStorage.getItem(nudgeKey)) return;
      sessionStorage.setItem(nudgeKey, '1');
      toast.info('Tell us your birthday so the team can wish you 🎂  — open the sidebar profile to add your dates.', { duration: 8000 });
    } catch (err) {
      // Never let a friendly nudge block the page render
      console.warn('dates-nudge skipped:', err);
    }
  }, [users.length, currentUser.id]);

  const fetchData = async () => {
    try {
      const [tasksRes, clientsRes, usersRes, timersRes, ctlRes, servicesRes] = await Promise.all([
        api.get('/tasks'), api.get('/clients'), api.get('/users'), api.get('/timers/active'), api.get('/client-time-logs'), api.get('/services').catch(() => ({ data: [] }))
      ]);
      setTasks(tasksRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
      setMyCtlLogs(ctlRes.data || []);
      setServices(servicesRes.data || []);
      const timerMap = {};
      timersRes.data.forEach(t => {
        timerMap[t.task_id] = { started_at: t.started_at, status: t.status || 'running', accumulated_seconds: t.accumulated_seconds || 0 };
      });
      setActiveTimers(timerMap);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  };

  const checkPreviousDay = async () => {
    try {
      const res = await api.get('/leave/check-previous');
      if (res.data.needs_action) {
        setLeaveDate(res.data.date);
        setShowLeavePrompt(true);
      }
    } catch (err) { console.warn('Previous-leave check failed:', err); }
  };

  const submitManualEntry = async () => {
    if (!manualTaskId || !manualHours || !manualReason) {
      toast.error('Fill all fields: hours, reason'); return;
    }
    const h = parseFloat(manualHours);
    if (isNaN(h) || h <= 0 || h > 10) {
      toast.error('Hours must be between 0.1 and 10'); return;
    }
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().slice(0, 10);
    const logDate = manualDate === 'yesterday' ? yesterdayStr : todayStr;
    try {
      await api.post(`/tasks/${manualTaskId}/time-log`, {
        hours: h, description: manualDesc || `Manual: ${MANUAL_REASONS.find(r => r.value === manualReason)?.label}`,
        entry_type: 'manual', manual_reason: manualReason, log_date: logDate
      });
      toast.success(`${h}h logged manually`);
      setManualTaskId(null); setManualHours(''); setManualReason(''); setManualDesc(''); setManualDate('today');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add manual entry'); }
  };

  const markLeave = async (date) => {
    try {
      await api.post(`/leave?date=${date}&reason=Leave`);
      toast.success(`Leave marked for ${date}`);
      setShowLeavePrompt(false);
      setShowLeaveDialog(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark leave'); }
  };

  // Timer display updater
  useEffect(() => {
    const interval = setInterval(() => {
      const displays = {};
      Object.entries(activeTimers).forEach(([taskId, timer]) => {
        let totalSeconds = timer.accumulated_seconds || 0;
        if (timer.status === 'running') {
          totalSeconds += (Date.now() - new Date(timer.started_at).getTime()) / 1000;
        }
        totalSeconds = Math.floor(totalSeconds);
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        displays[taskId] = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      });
      setTimerDisplays(displays);
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTimers]);

  const getClientName = (id) => clients.find(c => c.id === id)?.company || '';

  // Timer actions
  const startTimer = async (taskId) => {
    try {
      const res = await api.post(`/tasks/${taskId}/timer/start`);
      setActiveTimers(prev => {
        const next = {};
        Object.entries(prev).forEach(([id, t]) => {
          next[id] = id !== taskId && t.status === 'running' ? { ...t, status: 'paused' } : t;
        });
        next[taskId] = { started_at: res.data.started_at, status: 'running', accumulated_seconds: res.data.accumulated_seconds || 0 };
        return next;
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start timer'); }
  };

  const pauseTimer = async (taskId) => {
    try {
      const res = await api.post(`/tasks/${taskId}/timer/pause`);
      setActiveTimers(prev => ({ ...prev, [taskId]: { ...prev[taskId], status: 'paused', accumulated_seconds: res.data.accumulated_seconds } }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to pause timer'); }
  };

  const stopTimer = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/timer/stop`);
      setActiveTimers(prev => { const n = { ...prev }; delete n[taskId]; return n; });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to stop timer'); }
  };

  // Status update
  const handleStatus = async (taskId, newStatus, pendingMemberId = null) => {
    if (newStatus === 'pending_with_member' && !pendingMemberId) {
      toast.error('Please select which team member the task is pending with.');
      return;
    }
    try {
      const payload = { status: newStatus };
      if (newStatus === 'pending_with_member') payload.pending_with_member_id = pendingMemberId;
      const res = await api.put(`/tasks/${taskId}/status`, payload);
      if (newStatus === 'completed' && res.data?.message) {
        toast.success('Task marked complete' + (activeTimers[taskId] ? ' (timer auto-stopped & logged)' : ''));
      }
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to update status';
      if (msg.includes('time logged')) {
        toast.warning('You need to log some time first before completing this task. Start the timer for at least a minute, or log time manually.', { duration: 6000 });
      } else {
        toast.error(msg);
      }
    }
  };

  // Quick comment
  const handleComment = async (taskId) => {
    if (!quickComment.trim()) return;
    try {
      await api.post(`/tasks/${taskId}/comments`, { text: quickComment.trim() });
      toast.success('Note added');
      setQuickComment('');
      setCommentTaskId(null);
    } catch { toast.error('Failed to add note'); }
  };

  // Transfer a task to a teammate. The recipient must accept/reject before
  // it actually moves — same workflow as the Tasks page (request → accept).
  const handleSubmitTransfer = async () => {
    if (!transferTask || !transferToId) return;
    try {
      await api.post(`/tasks/${transferTask.id}/transfer-request`, {
        to_user_id: transferToId,
        comment: transferComment || null,
      });
      const target = users.find(u => u.id === transferToId);
      toast.success(`Transfer request sent to ${target?.full_name || 'user'}. Awaiting acceptance.`);
      setTransferTask(null);
      setTransferToId('');
      setTransferComment('');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send transfer request');
    }
  };

  // End-of-day check
  const handleLogout = () => {
    const hasRunningTimers = Object.values(activeTimers).some(t => t.status === 'running');
    const activeTasks = tasks.filter(t => t.status !== 'completed');
    const noTimeTasks = activeTasks.filter(t => (t.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0) <= 0);

    if (hasRunningTimers || noTimeTasks.length > 0) {
      setShowEodDialog(true);
      return;
    }
    doLogout();
  };

  const doLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  // Categorize tasks
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart); todayEnd.setDate(todayEnd.getDate() + 1);

  const activeTasks = tasks.filter(t => t.status !== 'completed');
  const completedTasks = tasks
    .filter(t => t.status === 'completed')
    .sort((a, b) => new Date(b.updated_at || b.completed_at || b.created_at) - new Date(a.updated_at || a.completed_at || a.created_at));
  const overdueTasks = activeTasks.filter(t => new Date(t.deadline) < todayStart);
  const todayTasks = activeTasks.filter(t => {
    const d = new Date(t.deadline);
    return d >= todayStart && d < todayEnd;
  });
  const inProgressTasks = activeTasks.filter(t => t.status === 'in_progress' && !overdueTasks.includes(t) && !todayTasks.includes(t));
  const otherPendingTasks = activeTasks.filter(t => !overdueTasks.includes(t) && !todayTasks.includes(t) && !inProgressTasks.includes(t));

  const downloadCategoryXlsx = async (category, label) => {
    try {
      const res = await api.get(`/dashboard/tasks/export?category=${category}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `dashboard_${category}.xlsx`;
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} exported`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  // Running timer info
  const runningEntry = Object.entries(activeTimers).find(([, t]) => t.status === 'running');
  const runningTaskId = runningEntry?.[0];
  const runningTask = runningTaskId ? tasks.find(t => t.id === runningTaskId) : null;

  if (loading) return <div className="flex items-center justify-center h-full"><p className="text-slate-400">Loading...</p></div>;

  return (
    <div className="min-h-full bg-slate-50" data-testid="member-workspace">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900" data-testid="workspace-greeting">
            {currentUser.full_name?.split(' ')[0]}'s Tasks
          </h1>
          <p className="text-xs text-slate-400">{now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Active timer badge */}
          {runningTask && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5" data-testid="active-timer-badge">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm font-mono text-red-700">{timerDisplays[runningTaskId] || '00:00:00'}</span>
              <span className="text-xs text-red-500 max-w-[120px] truncate">{runningTask.title}</span>
              <button onClick={() => stopTimer(runningTaskId)} className="p-0.5 bg-red-500 text-white rounded hover:bg-red-600" data-testid="stop-active-timer">
                <Square className="h-3 w-3" />
              </button>
            </div>
          )}
          <Button size="sm" onClick={() => setShowCreateTask(true)}
            className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981] hover:from-[#4a5bc5] hover:to-[#0d9668] text-white shadow-md"
            data-testid="create-task-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Create Task
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setCtlForm({ client_id: '', category: 'meeting', log_date: new Date().toISOString().slice(0,10), hours: '', description: '' }); setShowCtlDialog(true); }}
            className="text-indigo-600 hover:text-indigo-700 border-indigo-300 hover:bg-indigo-50" data-testid="log-client-time-btn">
            <Clock className="h-4 w-4 mr-1.5" /> Log Client Time
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowMyDates(true)}
            className="text-pink-600 hover:text-pink-700 border-pink-300 hover:bg-pink-50" data-testid="open-my-dates-btn">
            <Cake className="h-4 w-4 mr-1.5" /> My dates
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowLeaveDialog(true)}
            className="text-slate-500 hover:text-slate-900 border-slate-300" data-testid="mark-leave-btn">
            <CalendarOff className="h-4 w-4 mr-1.5" /> Mark Leave
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500 hover:text-slate-900" data-testid="workspace-logout">
            <LogOut className="h-4 w-4 mr-1.5" /> End Day
          </Button>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        <CelebrationsBanner currentUserId={currentUser.id} />
        <UpcomingCelebrationsCard />
        {/* Quick stats */}
        <div className="flex flex-wrap items-center gap-3">
          {[
            { label: 'Overdue', count: overdueTasks.length, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', exportCategory: 'overdue' },
            { label: 'Due Today', count: todayTasks.length, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', exportCategory: 'due_today' },
            { label: 'In Progress', count: inProgressTasks.length, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
            { label: 'Upcoming', count: otherPendingTasks.length, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
            { label: 'Completed', count: completedTasks.length, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
          ].map(s => (
            <div key={s.label}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-lg border ${s.bg} ${s.border} ${s.label === 'Completed' ? 'cursor-pointer hover:shadow-sm transition' : ''}`}
              onClick={() => s.label === 'Completed' && setShowCompleted(v => !v)}
              data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <span className={`text-2xl font-bold ${s.color}`}>{s.count}</span>
              <span className="text-sm text-slate-600">{s.label}</span>
              {s.label === 'Completed' && completedTasks.length > 0 && (
                <span className="text-xs text-green-700 ml-1">{showCompleted ? '▲' : '▼'}</span>
              )}
              {s.exportCategory && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); downloadCategoryXlsx(s.exportCategory, s.label); }}
                  className="ml-1 p-1 rounded hover:bg-white/70 text-slate-400 hover:text-slate-700"
                  title={`Download ${s.label} as Excel`}
                  data-testid={`dl-${s.exportCategory}-btn`}
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          {/* Active-tasks (all non-completed) — separate chip so the download stays discoverable */}
          <button
            type="button"
            onClick={() => downloadCategoryXlsx('active', 'Active Tasks')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#5B6FD6]/30 bg-[#5B6FD6]/5 text-[#5B6FD6] hover:bg-[#5B6FD6]/10 text-xs font-medium"
            data-testid="dl-active-btn"
          >
            <Download className="h-3.5 w-3.5" /> All active
          </button>
        </div>

        <DailyTipCard />

        <CrossTeamTasksCard />

        {/* Overdue */}
        {overdueTasks.length > 0 && (
          <TaskSection title="Overdue" icon={<AlertTriangle className="h-4 w-4 text-red-500" />} accent="red" tasks={overdueTasks}
            getClientName={getClientName} activeTimers={activeTimers} timerDisplays={timerDisplays}
            startTimer={startTimer} pauseTimer={pauseTimer} stopTimer={stopTimer}
            handleStatus={handleStatus} commentTaskId={commentTaskId} setCommentTaskId={setCommentTaskId}
            quickComment={quickComment} setQuickComment={setQuickComment} handleComment={handleComment}
            users={users} setManualTaskId={setManualTaskId} onTransfer={setTransferTask}
            currentUser={currentUser} pwmExpandedTaskId={pwmExpandedTaskId} setPwmExpandedTaskId={setPwmExpandedTaskId} />
        )}

        {/* Due Today */}
        <TaskSection title="Today" icon={<Clock className="h-4 w-4 text-amber-500" />} accent="amber" tasks={todayTasks}
          getClientName={getClientName} activeTimers={activeTimers} timerDisplays={timerDisplays}
          startTimer={startTimer} pauseTimer={pauseTimer} stopTimer={stopTimer}
          handleStatus={handleStatus} commentTaskId={commentTaskId} setCommentTaskId={setCommentTaskId}
          quickComment={quickComment} setQuickComment={setQuickComment} handleComment={handleComment}
          users={users} setManualTaskId={setManualTaskId} onTransfer={setTransferTask}
          currentUser={currentUser} pwmExpandedTaskId={pwmExpandedTaskId} setPwmExpandedTaskId={setPwmExpandedTaskId} />

        {/* In Progress */}
        {inProgressTasks.length > 0 && (
          <TaskSection title="In Progress" icon={<Play className="h-4 w-4 text-blue-500" />} accent="blue" tasks={inProgressTasks}
            getClientName={getClientName} activeTimers={activeTimers} timerDisplays={timerDisplays}
            startTimer={startTimer} pauseTimer={pauseTimer} stopTimer={stopTimer}
            handleStatus={handleStatus} commentTaskId={commentTaskId} setCommentTaskId={setCommentTaskId}
            quickComment={quickComment} setQuickComment={setQuickComment} handleComment={handleComment}
            users={users} setManualTaskId={setManualTaskId} onTransfer={setTransferTask}
            currentUser={currentUser} pwmExpandedTaskId={pwmExpandedTaskId} setPwmExpandedTaskId={setPwmExpandedTaskId} />
        )}

        {/* Upcoming / Other Pending */}
        {otherPendingTasks.length > 0 && (
          <TaskSection title="Upcoming" icon={<CalendarOff className="h-4 w-4 text-slate-500" />} accent="slate" tasks={otherPendingTasks}
            getClientName={getClientName} activeTimers={activeTimers} timerDisplays={timerDisplays}
            startTimer={startTimer} pauseTimer={pauseTimer} stopTimer={stopTimer}
            handleStatus={handleStatus} commentTaskId={commentTaskId} setCommentTaskId={setCommentTaskId}
            quickComment={quickComment} setQuickComment={setQuickComment} handleComment={handleComment}
            users={users} setManualTaskId={setManualTaskId} onTransfer={setTransferTask}
            currentUser={currentUser} pwmExpandedTaskId={pwmExpandedTaskId} setPwmExpandedTaskId={setPwmExpandedTaskId} />
        )}

        {/* My day summary - bottom of workspace */}
        <MyDaySummaryCard />

        {/* All clear */}
        {overdueTasks.length === 0 && todayTasks.length === 0 && inProgressTasks.length === 0 && otherPendingTasks.length === 0 && (
          <div className="text-center py-16" data-testid="all-clear">
            <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
            <p className="text-lg font-semibold text-slate-700">All caught up!</p>
            <p className="text-sm text-slate-400 mt-1">
              {completedTasks.length > 0
                ? `${completedTasks.length} task${completedTasks.length > 1 ? 's' : ''} completed — click the "Completed" card above to view them.`
                : 'No overdue, today, or in-progress tasks'}
            </p>
          </div>
        )}

        {/* Completed Tasks (collapsible) */}
        {completedTasks.length > 0 && showCompleted && (
          <div className="bg-white border border-green-200 rounded-lg p-4" data-testid="completed-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Completed Tasks
                <span className="text-xs font-normal text-slate-500">({completedTasks.length})</span>
              </h2>
              <button onClick={() => setShowCompleted(false)}
                className="text-xs text-slate-400 hover:text-slate-700">
                Hide
              </button>
            </div>
            <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
              {completedTasks.map(t => {
                const totalLogged = (t.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0);
                const completedOn = t.updated_at || t.completed_at;
                return (
                  <div key={t.id} className="py-2 flex items-center gap-3" data-testid={`completed-task-${t.id}`}>
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{t.title}</p>
                      <p className="text-xs text-slate-400 truncate flex items-center gap-2 flex-wrap">
                        <span>{getClientName(t.client_id)}</span>
                        {completedOn && (
                          <span className="text-green-600">
                            ✓ {new Date(completedOn).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                        {totalLogged > 0 && <span>· {totalLogged.toFixed(1)}h logged</span>}
                      </p>
                    </div>
                    <Badge className="bg-green-100 text-green-700 text-xs border-green-200 flex-shrink-0">Done</Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* End of Day Dialog */}
      <Dialog open={showEodDialog} onOpenChange={setShowEodDialog}>
        <DialogContent className="max-w-md" data-testid="eod-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Before you go</DialogTitle>
            <DialogDescription className="sr-only">End of day checklist</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {Object.values(activeTimers).some(t => t.status === 'running') && (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200" data-testid="eod-timer-warning">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <p className="text-red-700 font-medium">You have a running timer. Stop it to save your time.</p>
              </div>
            )}
            {(() => {
              const noTimeTasks = activeTasks.filter(t => (t.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0) <= 0);
              return noTimeTasks.length > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="eod-notime-warning">
                  <p className="text-amber-700 font-medium mb-1">{noTimeTasks.length} task{noTimeTasks.length > 1 ? 's' : ''} with no time logged:</p>
                  <ul className="text-amber-600 text-xs space-y-0.5">
                    {noTimeTasks.slice(0, 5).map(t => <li key={t.id}>- {t.title}</li>)}
                    {noTimeTasks.length > 5 && <li>...and {noTimeTasks.length - 5} more</li>}
                  </ul>
                </div>
              );
            })()}
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowEodDialog(false)} data-testid="eod-go-back">Go Back</Button>
            <Button className="flex-1 bg-slate-700 hover:bg-slate-800" onClick={doLogout} data-testid="eod-logout-anyway">Logout Anyway</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Task Dialog (Jun 2026) */}
      <Dialog open={!!transferTask} onOpenChange={(o) => { if (!o) { setTransferTask(null); setTransferToId(''); setTransferComment(''); } }}>
        <DialogContent className="max-w-md" data-testid="member-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-[#5B6FD6]" /> Transfer Task
            </DialogTitle>
            <DialogDescription>
              Transfer &quot;{transferTask?.title}&quot; to a teammate. They must accept before the task actually moves.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Transfer to <span className="text-red-600">*</span></Label>
              <Select value={transferToId} onValueChange={setTransferToId}>
                <SelectTrigger data-testid="member-transfer-target-select">
                  <SelectValue placeholder="Select teammate" />
                </SelectTrigger>
                <SelectContent>
                  {(users || []).filter(u => u.is_active !== false && u.id && u.id !== transferTask?.assignee_id && u.id !== currentUser?.id).map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name} {u.role === 'admin' ? '(Admin)' : u.role === 'super_admin' ? '(Super Admin)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea value={transferComment} onChange={(e) => setTransferComment(e.target.value)}
                placeholder="Add context for the receiver…"
                className="min-h-[80px]" data-testid="member-transfer-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTransferTask(null); setTransferToId(''); setTransferComment(''); }}>Cancel</Button>
            <Button className="bg-[#5B6FD6] hover:bg-[#4338CA]" onClick={handleSubmitTransfer} disabled={!transferToId}
              data-testid="member-submit-transfer-btn">
              Send Transfer Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Previous Day Leave Prompt */}
      <Dialog open={showLeavePrompt} onOpenChange={setShowLeavePrompt}>
        <DialogContent className="max-w-sm" data-testid="leave-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Action Required</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              No time was logged for <span className="font-semibold text-slate-700">{leaveDate}</span>. Timer logging is mandatory. Please mark leave or log time for yesterday before continuing.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-1">
            <Button variant="outline" className="flex-1" onClick={() => setShowLeavePrompt(false)} data-testid="leave-prompt-skip">
              I'll Log Time
            </Button>
            <Button className="flex-1 bg-[#5B6FD6] hover:bg-[#4A5BC4]" onClick={() => markLeave(leaveDate)} data-testid="leave-prompt-mark">
              <CalendarOff className="h-4 w-4 mr-1.5" /> Mark Leave
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog (members can self-assign; reviewer auto-set to admin) */}
      <CreateTaskDialog open={showCreateTask} onClose={() => setShowCreateTask(false)}
        clients={clients} users={users} services={services} canCreate={true}
        currentUser={currentUser} onCreated={fetchData} />


      {/* Log Client Time Dialog */}
      <Dialog open={showCtlDialog} onOpenChange={setShowCtlDialog}>
        <DialogContent className="max-w-md" data-testid="ctl-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-indigo-600" />
              Log Client Time
            </DialogTitle>
            <DialogDescription>
              Log downtime, meetings, or other non-task time against a client. Hours are non-billable but appear in the timesheet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Client <span className="text-red-500">*</span></Label>
              <Select value={ctlForm.client_id} onValueChange={(v) => setCtlForm({ ...ctlForm, client_id: v })}>
                <SelectTrigger data-testid="ctl-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Category <span className="text-red-500">*</span></Label>
              <Select value={ctlForm.category} onValueChange={(v) => setCtlForm({ ...ctlForm, category: v })}>
                <SelectTrigger data-testid="ctl-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="downtime">Downtime</SelectItem>
                  <SelectItem value="gst_visit">GST Visit</SelectItem>
                  <SelectItem value="call_for_data">Call for Data</SelectItem>
                  <SelectItem value="transition">Transition</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={ctlForm.log_date}
                  onChange={(e) => setCtlForm({ ...ctlForm, log_date: e.target.value })}
                  data-testid="ctl-date" />
              </div>
              <div>
                <Label className="text-sm">Hours <span className="text-red-500">*</span></Label>
                <Input type="number" step="0.25" min="0.25" max="24"
                  value={ctlForm.hours}
                  onChange={(e) => setCtlForm({ ...ctlForm, hours: e.target.value })}
                  placeholder="e.g. 1.5"
                  data-testid="ctl-hours" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Textarea rows={2} value={ctlForm.description}
                onChange={(e) => setCtlForm({ ...ctlForm, description: e.target.value })}
                placeholder="What was discussed / why this time was logged"
                data-testid="ctl-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCtlDialog(false)}>Cancel</Button>
            <Button
              data-testid="ctl-submit"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={savingCtl || !ctlForm.client_id || !ctlForm.log_date || !ctlForm.hours || parseFloat(ctlForm.hours) <= 0}
              onClick={async () => {
                setSavingCtl(true);
                try {
                  await api.post('/client-time-logs', {
                    client_id: ctlForm.client_id,
                    category: ctlForm.category,
                    log_date: ctlForm.log_date,
                    hours: parseFloat(ctlForm.hours),
                    description: ctlForm.description || null,
                  });
                  toast.success('Client time logged');
                  setShowCtlDialog(false);
                  fetchData();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to log time');
                } finally {
                  setSavingCtl(false);
                }
              }}
            >
              {savingCtl ? 'Saving…' : 'Log Time'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Mark Leave Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="max-w-xs" data-testid="mark-leave-dialog">
          <DialogHeader>
            <DialogTitle>Mark Leave</DialogTitle>
            <DialogDescription className="sr-only">Mark leave for today or yesterday</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Select the date you were on leave:</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => markLeave(new Date().toISOString().slice(0, 10))} data-testid="leave-today-btn">
                Today
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => {
                const y = new Date(); y.setDate(y.getDate() - 1);
                markLeave(y.toISOString().slice(0, 10));
              }} data-testid="leave-yesterday-btn">
                Yesterday
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Time Entry Dialog */}
      <Dialog open={!!manualTaskId} onOpenChange={(open) => { if (!open) setManualTaskId(null); }}>
        <DialogContent className="max-w-sm" data-testid="manual-entry-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-4 w-4 text-[#5B6FD6]" /> Manual Time Entry
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              {tasks.find(t => t.id === manualTaskId)?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Hours (max 10/day)</label>
              <Input type="number" min="0.1" max="10" step="0.25" value={manualHours}
                onChange={e => setManualHours(e.target.value)} placeholder="e.g. 2.5"
                data-testid="manual-hours-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Reason *</label>
              <Select value={manualReason} onValueChange={setManualReason}>
                <SelectTrigger data-testid="manual-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  {MANUAL_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value} data-testid={`reason-${r.value}`}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Date</label>
              <Select value={manualDate} onValueChange={setManualDate}>
                <SelectTrigger data-testid="manual-date-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Description (optional)</label>
              <Input value={manualDesc} onChange={e => setManualDesc(e.target.value)}
                placeholder="Brief note..." data-testid="manual-desc-input" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setManualTaskId(null)}>Cancel</Button>
              <Button className="flex-1 bg-[#5B6FD6] hover:bg-[#4A5BC4]" onClick={submitManualEntry}
                data-testid="manual-entry-submit">Log Time</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* My birthday & joining date dialog */}
      <MyProfileDatesDialog
        open={showMyDates}
        onOpenChange={setShowMyDates}
        initialDOB={(users.find(u => u.id === currentUser.id) || {}).date_of_birth || ''}
        initialJoin={(users.find(u => u.id === currentUser.id) || {}).joining_date || ''}
        onSaved={fetchData}
      />
    </div>
  );
}


// --- Task Section ---
function TaskSection({ title, icon, accent, tasks, getClientName, activeTimers, timerDisplays,
  startTimer, pauseTimer, stopTimer, handleStatus, commentTaskId, setCommentTaskId,
  quickComment, setQuickComment, handleComment, users, setManualTaskId, onTransfer,
  currentUser, pwmExpandedTaskId, setPwmExpandedTaskId }) {

  if (tasks.length === 0) {
    return (
      <div className={`rounded-xl border border-${accent}-200 bg-${accent}-50/30 p-4`} data-testid={`section-${title.toLowerCase().replace(/ /g, '-')}`}>
        <div className="flex items-center gap-2 mb-1">{icon}<h2 className="font-semibold text-slate-800">{title}</h2></div>
        <p className="text-sm text-slate-400 ml-6">No tasks</p>
      </div>
    );
  }

  return (
    <div data-testid={`section-${title.toLowerCase().replace(/ /g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h2 className="font-semibold text-slate-800">{title}</h2>
        <Badge variant="outline" className="text-xs">{tasks.length}</Badge>
      </div>
      <div className="space-y-1">
        {tasks.map(task => (
          <TaskRow key={task.id} task={task} getClientName={getClientName}
            activeTimers={activeTimers} timerDisplays={timerDisplays}
            startTimer={startTimer} pauseTimer={pauseTimer} stopTimer={stopTimer}
            handleStatus={handleStatus} commentTaskId={commentTaskId}
            setCommentTaskId={setCommentTaskId} quickComment={quickComment}
            setQuickComment={setQuickComment} handleComment={handleComment} users={users}
            currentUser={currentUser}
            pwmExpandedTaskId={pwmExpandedTaskId} setPwmExpandedTaskId={setPwmExpandedTaskId}
            setManualTaskId={setManualTaskId} onTransfer={onTransfer} />
        ))}
      </div>
    </div>
  );
}


// --- Single Task Row ---
function TaskRow({ task, getClientName, activeTimers, timerDisplays,
  startTimer, pauseTimer, stopTimer, handleStatus,
  commentTaskId, setCommentTaskId, quickComment, setQuickComment, handleComment, users, setManualTaskId,
  onTransfer,
  currentUser, pwmExpandedTaskId, setPwmExpandedTaskId }) {

  const timer = activeTimers[task.id];
  const isRunning = timer?.status === 'running';
  const isPaused = timer?.status === 'paused';
  const totalLogged = (task.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0);
  const manualLogged = (task.time_logs || []).filter(l => l.entry_type === 'manual').reduce((s, l) => s + (l.hours || 0), 0);
  const pendingWithMe = task.status === 'pending_with_member' && task.pending_with_member_id === currentUser?.id;

  return (
    <div className={`group flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-colors ${pendingWithMe ? 'bg-purple-50 border-l-4 border-l-purple-400 border-y-purple-100 border-r-purple-100' : 'bg-white border-slate-200 hover:border-slate-300'}`} data-testid={`task-row-${task.id}`}>
      {/* Task info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
          <span className="truncate">{task.title}</span>
          {task.is_recurring && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 ring-1 ring-violet-200 flex-shrink-0"
              title={`Recurring · ${task.recurrence_type || 'daily'} (auto-creates next instance on completion)`}
              data-testid={`recurring-badge-${task.id}`}
            >
              <RotateCcw className="h-2.5 w-2.5" />
              {task.recurrence_type || 'recurring'}
            </span>
          )}
        </p>
        <p className="text-xs text-slate-400 truncate flex items-center gap-1.5 flex-wrap">
          <span>{getClientName(task.client_id)}</span>
          {task.deadline && (() => {
            const dl = new Date(task.deadline);
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const tom = new Date(today); tom.setDate(tom.getDate() + 1);
            const isOverdue = dl < today;
            const isToday = dl >= today && dl < tom;
            const cls = isOverdue ? 'text-red-600 bg-red-50' : isToday ? 'text-amber-700 bg-amber-50' : 'text-slate-600 bg-slate-100';
            return (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${cls}`} data-testid={`deadline-${task.id}`}>
                <Calendar className="h-3 w-3" />
                Due {dl.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: dl.getFullYear() !== today.getFullYear() ? 'numeric' : undefined })}
              </span>
            );
          })()}
          {(() => {
            const ref = task.updated_at || task.created_at;
            if (!ref) return null;
            const days = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
            return <span className={`font-medium ${days > 15 ? 'text-red-500' : days > 7 ? 'text-amber-500' : 'text-slate-400'}`} data-testid={`ageing-${task.id}`}>{days}d old</span>;
          })()}
          {totalLogged > 0 && <span>· {totalLogged.toFixed(1)}h logged</span>}
          {manualLogged > 0 && <span className="text-amber-500">({manualLogged.toFixed(1)}h manual)</span>}
        </p>
      </div>

      {/* Timer - one click */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {isRunning ? (
          <>
            <span className="text-xs font-mono text-red-600 bg-red-50 px-1.5 py-0.5 rounded min-w-[60px] text-center">{timerDisplays[task.id] || '00:00:00'}</span>
            <button onClick={() => pauseTimer(task.id)} className="p-1 bg-amber-500 text-white rounded hover:bg-amber-600" data-testid={`pause-${task.id}`} title="Pause">
              <Pause className="h-3 w-3" />
            </button>
            <button onClick={() => stopTimer(task.id)} className="p-1 bg-red-500 text-white rounded hover:bg-red-600" data-testid={`stop-${task.id}`} title="Stop & Log">
              <Square className="h-3 w-3" />
            </button>
          </>
        ) : isPaused ? (
          <>
            <span className="text-xs font-mono text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded animate-pulse min-w-[60px] text-center">{timerDisplays[task.id] || '00:00:00'}</span>
            <button onClick={() => startTimer(task.id)} className="p-1 bg-emerald-500 text-white rounded hover:bg-emerald-600" data-testid={`resume-${task.id}`} title="Resume">
              <Play className="h-3 w-3" />
            </button>
            <button onClick={() => stopTimer(task.id)} className="p-1 bg-red-500 text-white rounded hover:bg-red-600" data-testid={`stop-${task.id}`} title="Stop & Log">
              <Square className="h-3 w-3" />
            </button>
          </>
        ) : (
          <button onClick={() => startTimer(task.id)} className="flex items-center gap-1 text-xs bg-emerald-500 text-white px-2.5 py-1.5 rounded-md hover:bg-emerald-600 transition-colors" data-testid={`start-${task.id}`}>
            <Play className="h-3 w-3" /> Start
          </button>
        )}
      </div>

      {/* Manual entry button */}
      <button onClick={() => setManualTaskId(task.id)}
        className="p-1.5 text-slate-300 hover:text-[#5B6FD6] hover:bg-[#5B6FD6]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        data-testid={`manual-entry-${task.id}`} title="Manual time entry">
        <Edit3 className="h-3.5 w-3.5" />
      </button>

      {/* Transfer button (Jun 2026): handoff a task to a teammate with
          accept/reject before the move is final. */}
      <button onClick={() => onTransfer && onTransfer(task)}
        className="p-1.5 text-slate-300 hover:text-[#5B6FD6] hover:bg-[#5B6FD6]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        data-testid={`transfer-task-btn-${task.id}`} title="Transfer task">
        <ArrowRightLeft className="h-3.5 w-3.5" />
      </button>

      {/* Status - one click dropdown */}
      <AutoPendingBadge task={task} />
      <Popover>
        <PopoverTrigger asChild>
          <button className={`inline-flex items-center gap-1 text-xs text-white px-2.5 py-1.5 rounded-full cursor-pointer hover:opacity-90 transition-opacity ${STATUS_COLORS[task.status] || 'bg-slate-500'} flex-shrink-0`} data-testid={`status-${task.id}`}>
            <span>{STATUS_LABELS[task.status] || task.status}</span>
            {task.status === 'pending_with_member' && task.pending_with_member_id && (() => {
              const u = (users || []).find(x => x.id === task.pending_with_member_id);
              const name = u?.full_name?.split(' ')[0] || 'Unknown';
              return <span className="bg-white/25 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" data-testid={`pending-member-name-${task.id}`}>{name}</span>;
            })()}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1" align="end">
          {STATUS_OPTIONS.map(s => (
            s.value === 'pending_with_member' ? (
              <div key={s.value} className="rounded">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPwmExpandedTaskId(prev => prev === task.id ? null : task.id); }}
                  className={`w-full text-left text-sm px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 ${task.status === s.value ? 'font-semibold bg-slate-50' : ''}`}
                  data-testid={`opt-${task.id}-${s.value}`}
                >
                  <div className={`w-2 h-2 rounded-full ${s.color}`} /> {s.label}
                  <ChevronRight className={`h-3.5 w-3.5 ml-auto text-slate-400 transition-transform ${pwmExpandedTaskId === task.id ? 'rotate-90' : ''}`} />
                </button>
                {pwmExpandedTaskId === task.id && (
                  <div className="pl-5 pr-1 py-1 max-h-44 overflow-y-auto" data-testid={`pwm-picker-${task.id}`}>
                    {(() => {
                      // Use the fresh user record from the API (users state) rather than the
                      // cached localStorage copy — parent_admin_id may have changed since login.
                      const meFresh = (users || []).find(u => u.id === currentUser.id) || currentUser;
                      const meId = meFresh.id;
                      const myAdminId = meFresh.parent_admin_id;
                      const eligible = (users || []).filter(u => {
                        if (!u.id || u.is_active === false) return false;
                        if (u.id === meId) return false;
                        if (u.role === 'super_admin') return true;
                        if (myAdminId && u.id === myAdminId) return true;
                        if (myAdminId && u.parent_admin_id === myAdminId && u.role === 'member') return true;
                        return false;
                      });
                      if (eligible.length === 0) {
                        return <div className="text-xs text-slate-400 px-2 py-1">No team members available</div>;
                      }
                      return eligible.map(u => (
                        <button key={u.id} onClick={() => { setPwmExpandedTaskId(null); handleStatus(task.id, 'pending_with_member', u.id); }}
                          className="w-full text-left text-xs px-2 py-1 rounded hover:bg-[#5B6FD6]/10 text-slate-700"
                          data-testid={`pwm-${task.id}-${u.id}`}>
                          {u.full_name}
                          {u.role === 'super_admin' && <span className="ml-1 text-[10px] text-red-500">(Super Admin)</span>}
                          {u.id === myAdminId && <span className="ml-1 text-[10px] text-slate-400">(Admin)</span>}
                        </button>
                      ));
                    })()}
                  </div>
                )}
              </div>
            ) : (
              <button key={s.value} onClick={() => handleStatus(task.id, s.value)}
                className={`w-full text-left text-sm px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 ${task.status === s.value ? 'font-semibold bg-slate-50' : ''}`}
                data-testid={`opt-${task.id}-${s.value}`}>
                <div className={`w-2 h-2 rounded-full ${s.color}`} /> {s.label}
              </button>
            )
          ))}
        </PopoverContent>
      </Popover>

      {/* Inline comment */}
      {commentTaskId === task.id ? (
        <div className="flex items-center gap-1 flex-shrink-0">
          <Input value={quickComment} onChange={e => setQuickComment(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleComment(task.id); if (e.key === 'Escape') setCommentTaskId(null); }}
            placeholder="Note..." className="h-7 text-xs w-32" autoFocus data-testid={`comment-input-${task.id}`} />
          <button onClick={() => handleComment(task.id)} className="p-1 text-[#5B6FD6] hover:bg-[#5B6FD6]/10 rounded" data-testid={`comment-send-${task.id}`}>
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button onClick={() => { setCommentTaskId(task.id); setQuickComment(''); }}
          className="p-1.5 text-slate-300 hover:text-[#5B6FD6] hover:bg-[#5B6FD6]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          data-testid={`comment-btn-${task.id}`} title="Add note">
          <Send className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
